import { useCallback, useEffect, useState } from 'react';
import type { GridStackWidget } from 'gridstack';
import { updateParameter } from '../../Components/CodeEditor/Devices/DeviceCardBuilder';
import { getMainProgramCode } from '../../Components/CodeEditor/utils/mainProgramEditor';
import { writeConstructorArgInProgramCode } from '../../utils/deviceProgramCodeUtils';
import type { DeviceCardProps } from '../../Components/CodeEditor/Devices/DeviceCardProps';
import type { DashboardWidgetContent } from '../../Components/Dashboard/dashboardTypes';
import {
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  sensorClassNameFromStreamKey,
  streamPortFromSensorKey,
} from '../../Components/Graphs/sensor-utils';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { projectSlice } from '../../redux/ProjectSlice';
import type { AppDispatch } from '../../redux/Store';
import type { DriveUnitPortOccupancy } from '../../redux/ApplicationSlice';
import { persistDashboardLayoutToProject } from '../../Components/Dashboard/persistDashboardWidgetContent';
import { buildPersistableDashboardContext } from '../../utils/dashboardWidgetUtils';
import { DRIVE_UNIT_PORT_PARAMETERS } from '../../utils/driveUnitPortSelection';
import { isDriveUnitPortParameterName } from '../../utils/frameworkArgTypes';
import { remapDriveUnitPortInWidgetContent, remapDriveUnitPortInStreamKey } from '../../utils/dashboardWidgetUtils';
import { collectElectronicsVariableNamesOnPort } from '../../utils/electronicsPortFromProgramCode';
import { frameworkClassNameFromElectronicKeyOrPrefix } from '../../utils/electronicKeyUtils';
import {
  dedupeStreamKeysPreserveOrder,
  parseDashboardWidgetContent,
  parseDataStreamRequestList,
  rebuildDashboardProgramCodeFromLayout,
  replaceDataStreamRequestListInCode,
  stableKeysSignature,
} from '../../utils/sensor-stream-tracker';

type ConfigurePanelPortChangeProps = Pick<
  DeviceCardProps,
  'id' | 'parameterName' | 'editor' | 'lineNumber' | 'parentName' | 'parentClass' | 'mechanismClass'
> & {
  skipEditorParameterSync?: boolean;
};

/** Parses `electronicId:parameterName` (e.g. `highspeedmotor-m1:motor_port`) to the device variable name. */
function variableNameFromElectronicKey(electronic: string): string {
  const id = electronic.slice(0, electronic.lastIndexOf(':'));
  const dashIdx = id.indexOf('-');
  return dashIdx >= 0 ? id.slice(dashIdx + 1) : id;
}

function streamKeyUsesPort(sensorKey: string, port: number): boolean {
  const usesDriveUnitPort =
    isMotorStreamSensorKey(sensorKey) || isElectronicSensorStreamSensorKey(sensorKey) || isOtherElectronicsStreamSensorKey(sensorKey);
  return usesDriveUnitPort && streamPortFromSensorKey(sensorKey) === port;
}

function widgetContentUsesStreamPort(content: DashboardWidgetContent, port: number): boolean {
  if (streamKeyUsesPort(content.dataKey, port)) return true;
  return content.sensorKeys.some((key) => streamKeyUsesPort(key, port));
}

function widgetContentMatchesSensorClass(content: DashboardWidgetContent, sensorClassName: string | null): boolean {
  if (!sensorClassName) return false;
  const widgetSensorClassName = content.options?.sensorClassName;
  if (widgetSensorClassName) return widgetSensorClassName === sensorClassName;

  return [content.dataKey, ...content.sensorKeys].some((key) => sensorClassNameFromStreamKey(key) === sensorClassName);
}

function shouldRemapWidgetContentForElectronic(
  content: DashboardWidgetContent,
  variableName: string,
  oldPort: number,
  parameterName: string,
  sensorClassName: string | null
): boolean {
  if (!widgetContentUsesStreamPort(content, oldPort)) return false;
  if (parameterName === DRIVE_UNIT_PORT_PARAMETERS.MOTOR) {
    const widgetVariableName = content.options?.motorVariableName;
    if (widgetVariableName) return widgetVariableName === variableName;
  }
  if (parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR) {
    const widgetVariableName = content.options?.sensorVariableName;
    if (widgetVariableName) return widgetVariableName === variableName;
    return widgetContentMatchesSensorClass(content, sensorClassName);
  }
  return true;
}

function remapDashboardLayoutForElectronicPortChange(
  layout: GridStackWidget[],
  variableName: string,
  oldPort: number,
  newPort: number,
  parameterName: string,
  sensorClassName: string | null
): GridStackWidget[] {
  let changed = false;
  const next = layout.map((widget) => {
    const parsed = parseDashboardWidgetContent(widget.content);
    if (!parsed || !shouldRemapWidgetContentForElectronic(parsed, variableName, oldPort, parameterName, sensorClassName)) return widget;

    changed = true;
    return { ...widget, content: JSON.stringify(remapDriveUnitPortInWidgetContent(parsed, oldPort, newPort)) };
  });

  return changed ? next : layout;
}

function shouldRemapStreamKeyForElectronicPortChange(
  sensorKey: string,
  oldPort: number,
  newPort: number,
  parameterName: string,
  sensorClassName: string | null,
  variableName: string,
  baselineProgramCode: string,
  currentProgramCode: string
): boolean {
  if (!streamKeyUsesPort(sensorKey, oldPort)) return false;
  if (parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR) {
    if (sensorClassName && sensorClassNameFromStreamKey(sensorKey) !== sensorClassName) return false;
    return isElectronicSensorStreamSensorKey(sensorKey) || isOtherElectronicsStreamSensorKey(sensorKey);
  }
  if (parameterName === DRIVE_UNIT_PORT_PARAMETERS.MOTOR) {
    if (!isMotorStreamSensorKey(sensorKey) && !isOtherElectronicsStreamSensorKey(sensorKey)) return false;

    const electronicsOnOldPortBaseline = collectElectronicsVariableNamesOnPort(baselineProgramCode, oldPort, DRIVE_UNIT_PORT_PARAMETERS.MOTOR);
    if (electronicsOnOldPortBaseline.length === 1 && electronicsOnOldPortBaseline[0] === variableName) return true;

    const electronicsOnOldPortCurrent = collectElectronicsVariableNamesOnPort(currentProgramCode, oldPort, DRIVE_UNIT_PORT_PARAMETERS.MOTOR);
    const electronicsOnNewPort = collectElectronicsVariableNamesOnPort(currentProgramCode, newPort, DRIVE_UNIT_PORT_PARAMETERS.MOTOR);
    return electronicsOnOldPortCurrent.length === 0 && electronicsOnNewPort.length === 1 && electronicsOnNewPort[0] === variableName;
  }
  return false;
}

function remapProgramCodeDataStreamRequestListForPortChange(
  code: string,
  oldPort: number,
  newPort: number,
  parameterName: string,
  sensorClassName: string | null,
  variableName: string,
  baselineProgramCode: string
): string {
  const keys = parseDataStreamRequestList(code);
  if (!keys?.length) return code;

  const remapped = dedupeStreamKeysPreserveOrder(
    keys.map((key) =>
      shouldRemapStreamKeyForElectronicPortChange(key, oldPort, newPort, parameterName, sensorClassName, variableName, baselineProgramCode, code)
        ? remapDriveUnitPortInStreamKey(key, oldPort, newPort)
        : key
    )
  );
  if (stableKeysSignature(keys) === stableKeysSignature(remapped)) return code;
  return replaceDataStreamRequestListInCode(code, remapped);
}

/** Drive-unit port changes that also remap dashboard `set_request_list` stream keys. */
export function portChangeSyncsDashboardStreams(parameterName: string): boolean {
  return parameterName === DRIVE_UNIT_PORT_PARAMETERS.MOTOR || parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR;
}

/** Skips the next editor stream-sync after a configure-panel port commit (already synced). */
export const skipNextDriveUnitPortStreamSyncRef = { current: false };

/**
 * Remaps dashboard widgets and `set_request_list` when a drive-unit port changes (constructor
 * already in program code).
 *
 * Returns whether a program replacement was queued. Callers that suppress the follow-up
 * editor-driven sync must arm that suppression only when this returned `true`, because the
 * suppression is cleared by the Monaco edit the replacement produces.
 */
export function applyDriveUnitPortStreamSync(
  dispatch: AppDispatch,
  opts: {
    readOnly?: boolean;
    projectId?: string;
    dashboardLayout: GridStackWidget[] | undefined;
    /** Program text this sync should produce, with the port change already applied. */
    desiredProgramCode: string;
    /** Pre-edit snapshot used to decide which stream keys the port change owns. */
    baselineProgramCode: string;
    /**
     * Text currently in the Monaco buffer. A replacement is only queued when it would actually
     * change that buffer; comparing against `baselineProgramCode` instead would queue a no-op
     * replacement for every editor-driven sync, because the baseline predates whatever the user
     * just typed.
     */
    currentBufferCode: string;
    electronic: string;
    parameterName: string;
    oldPort: number;
    newPort: number;
    driveUnitPorts?: DriveUnitPortOccupancy[];
  }
): boolean {
  if (opts.readOnly || !opts.projectId) return false;

  const variableName = variableNameFromElectronicKey(opts.electronic);
  const sensorClassName =
    opts.parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR ? frameworkClassNameFromElectronicKeyOrPrefix(opts.electronic) : null;
  const layout = opts.dashboardLayout ?? [];
  const nextLayout = portChangeSyncsDashboardStreams(opts.parameterName)
    ? remapDashboardLayoutForElectronicPortChange(layout, variableName, opts.oldPort, opts.newPort, opts.parameterName, sensorClassName)
    : layout;
  const layoutChanged = nextLayout !== layout;

  if (layoutChanged) {
    const persistContext = buildPersistableDashboardContext(opts.driveUnitPorts ?? [], opts.desiredProgramCode);
    persistDashboardLayoutToProject(dispatch, nextLayout, persistContext);
  }

  let nextCode = opts.desiredProgramCode;
  if (layoutChanged && layout.length > 0) {
    nextCode = rebuildDashboardProgramCodeFromLayout(nextCode, nextLayout);
  }
  nextCode = remapProgramCodeDataStreamRequestListForPortChange(
    nextCode,
    opts.oldPort,
    opts.newPort,
    opts.parameterName,
    sensorClassName,
    variableName,
    opts.baselineProgramCode
  );
  if (nextCode === opts.currentBufferCode) return false;

  dispatch(
    projectSlice.actions.queueProgramReplacement({
      code: nextCode,
      projectId: opts.projectId,
      lastModified: new Date().toISOString(),
    })
  );
  return true;
}

export interface ConfigurePanelPortChange {
  commitPortChange: (portNumber: number) => void;
  /** True when the last click could not be written to the constructor, so nothing was changed. */
  portWriteBlocked: boolean;
}

/**
 * Commits a drive-unit port change from the configure hardware panel.
 * For drive-unit port parameters with dashboard stream sync (e.g. `motor_port`), updates the constructor in program
 * code and dashboard streams, then `setPort`; manual Monaco edits use `useDriveUnitPortStreamSyncFromEditor`.
 * Other parameters use Monaco `updateParameter`.
 *
 * The constructor write gates the stream sync. When the device declaration cannot be resolved (for example a
 * variable renamed in Monaco but not yet reparsed), the click is rejected outright: no constructor edit, no stream
 * remap, and no port selection change. Remapping `set_request_list` without the matching constructor write would
 * point the dashboard at a port no device is declared on.
 */
export function useConfigurePanelPortChange(
  props: ConfigurePanelPortChangeProps,
  port: number | undefined,
  setPort: (value: number) => void
): ConfigurePanelPortChange {
  const dispatch = useAppDispatch();
  const loadedProject = useAppSelector((state) => state.project.loadedProject);
  const driveUnitPorts = useAppSelector((state) => state.application?.driveUnitPorts ?? []);
  const readOnly = loadedProject?.readOnly;
  const projectId = loadedProject?.id;
  const dashboardLayout = loadedProject?.dashboardLayout;
  const programCode = loadedProject?.program?.code;
  const [portWriteBlocked, setPortWriteBlocked] = useState(false);

  // A rejected click reflects the source at the time of the click; once the program changes the reason may be gone.
  useEffect(() => {
    setPortWriteBlocked(false);
  }, [programCode]);

  const commitPortChange = useCallback(
    (portNumber: number) => {
      const oldPort = port;
      const isDriveUnitPortChange =
        isDriveUnitPortParameterName(props.parameterName) && typeof oldPort === 'number' && Number.isFinite(oldPort) && oldPort !== portNumber;
      const usesStreamSyncPath = isDriveUnitPortChange && portChangeSyncsDashboardStreams(props.parameterName);

      if (usesStreamSyncPath) {
        if (!readOnly && projectId && !props.skipEditorParameterSync) {
          const bufferCode = getMainProgramCode(props.editor, programCode ?? '');
          const constructorWrite = writeConstructorArgInProgramCode(
            bufferCode,
            props.lineNumber,
            props.parentName,
            props.parentClass,
            props.mechanismClass,
            props.parameterName,
            portNumber.toString()
          );
          if (!constructorWrite.applied) {
            setPortWriteBlocked(true);
            return;
          }

          setPortWriteBlocked(false);
          const queuedReplacement = applyDriveUnitPortStreamSync(dispatch, {
            readOnly,
            projectId,
            dashboardLayout,
            desiredProgramCode: constructorWrite.code,
            baselineProgramCode: bufferCode,
            currentBufferCode: bufferCode,
            electronic: `${props.id}:${props.parameterName}`,
            parameterName: props.parameterName,
            oldPort,
            newPort: portNumber,
            driveUnitPorts,
          });
          // Only suppress the follow-up editor sync when a replacement was actually queued. The
          // suppression is cleared by the Monaco edit that replacement produces, so arming it
          // without one leaves the flag set and silently drops a later editor-driven port change.
          if (queuedReplacement) {
            skipNextDriveUnitPortStreamSyncRef.current = true;
          }
        }
        setPort(portNumber);
        return;
      }

      setPort(portNumber);

      if (!props.skipEditorParameterSync) {
        updateParameter(props as DeviceCardProps, portNumber.toString());
      }
    },
    [dispatch, driveUnitPorts, readOnly, projectId, dashboardLayout, programCode, port, props, setPort]
  );

  return { commitPortChange, portWriteBlocked };
}
